import { useEffect, useRef, useState } from "react";
import ApiClass from "../api";
import persistedState from "../persited_store";
import utilityfun from "../helpers/utilityfun";
import { WritingReply } from "./WritingReply";
import { parseStructuredReply } from "./ChatStructuredReply";
import "../assets/css/askLoading.css";

const AGENTS = [
  { slug: "standard", name: "Standard Agent" },
  { slug: "question", name: "Question Agent" },
  { slug: "image", name: "Image Generate Agent" },
];

function loggedInName() {
  const user = persistedState.user();
  if (!user) return "";
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.fullName ||
    user.userName ||
    ""
  );
}

const isAdminUser = () =>
  utilityfun.isAdmin() || utilityfun.isDistrictAdmin() || utilityfun.isSchoolAdmin();

const GeneratingStatus = () => (
  <div className="ask-loading-bubble" aria-label="Writing">
    <div className="ask-loading-dots">
      <span />
      <span />
      <span />
    </div>
  </div>
);

export function ChatBot({ bottom = 16 }) {
  const showAskAi = isAdminUser();
  const [open, setOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [input, setInput] = useState("");
  const [askInput, setAskInput] = useState("");
  const [chatId, setChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [askMessages, setAskMessages] = useState([]);
  const [agentSlug, setAgentSlug] = useState("question");
  const endRef = useRef(null);
  const askEndRef = useRef(null);
  const inputRef = useRef(null);
  const askInputRef = useRef(null);
  const startingRef = useRef(false);

  const focusInput = (ref) => {
    requestAnimationFrame(() => ref.current?.focus());
  };

  const startChat = async () => {
    if (startingRef.current || chatId) return;
    startingRef.current = true;
    setLoading(true);
    try {
      const body = {};
      const name = loggedInName();
      if (name) body.name = name;
      const res = await ApiClass.postNodeRequest(
        "chatbot/start",
        !!persistedState.token(),
        body
      );
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setChatId(data.chatId);
        if (data?.reply) {
          setMessages((m) => [...m, { from: "bot", text: data.reply }]);
        }
      } else {
        startingRef.current = false;
        setMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      startingRef.current = false;
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setLoading(false);
      focusInput(inputRef);
    }
  };

  useEffect(() => {
    if (!open) return;
    focusInput(inputRef);
    startChat();
  }, [open]);

  useEffect(() => {
    if (!askOpen) return;
    if (!askMessages.length) {
      const agent = AGENTS.find((item) => item.slug === agentSlug);
      setAskMessages([
        {
          from: "bot",
          text: `Ask AI is using ${agent?.name || "the selected agent"} with its configured prompt, skills, and knowledge.`,
          instant: true,
        },
      ]);
    }
    focusInput(askInputRef);
  }, [askOpen]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    askEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [askMessages, askOpen]);

  useEffect(() => {
    const isChatWindow = (el) => {
      const w = parseInt(el?.style?.width || "0", 10);
      const h = parseInt(el?.style?.height || "0", 10);
      return w > 280 || h > 90;
    };

    const hideNativeLauncher = () => {
      try {
        window.$zopim?.livechat?.button?.hide?.();
      } catch {
        /* ignore */
      }
      document
        .querySelectorAll(
          'iframe#launcher, iframe[title="Button to launch messaging window"]'
        )
        .forEach((el) => el.style.setProperty("display", "none", "important"));
      document.querySelectorAll(".zopim").forEach((el) => {
        if (!isChatWindow(el)) el.style.setProperty("display", "none", "important");
      });
    };

    hideNativeLauncher();
    const retries = [300, 800, 1600, 3200, 6000].map((ms) =>
      setTimeout(hideNativeLauncher, ms)
    );
    const observer = new MutationObserver(hideNativeLauncher);
    observer.observe(document.body, { childList: true, subtree: true });
    if (window.$zopim) {
      window.$zopim(hideNativeLauncher);
    }
    return () => {
      retries.forEach(clearTimeout);
      observer.disconnect();
    };
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages((m) => [...m, { from: "user", text }]);
    setLoading(true);
    focusInput(inputRef);

    try {
      const body = { message: text, chatId };
      if (!chatId) {
        const name = loggedInName();
        if (name) body.name = name;
      }
      const res = await ApiClass.postNodeRequest(
        "chatbot/ask",
        !!persistedState.token(),
        body
      );
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setChatId(data.chatId);
        setMessages((m) => [...m, { from: "bot", text: data?.reply || "" }]);
      } else {
        setMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setLoading(false);
      focusInput(inputRef);
    }
  };

  const sendAskAiMessage = async (text) => {
    if (!text || askLoading) return;

    const history = askMessages
      .filter((item) => item.from === "user" || item.from === "bot")
      .map((item) => ({
        role: item.from === "bot" ? "assistant" : "user",
        content: item.text,
      }));

    setAskInput("");
    setAskMessages((m) => [...m, { from: "user", text }]);
    setAskLoading(true);
    focusInput(askInputRef);

    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai", true, {
        message: text,
        agentSlug,
        history,
      });
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        setAskMessages((m) => [...m, { from: "bot", text: data?.reply || "" }]);
      } else {
        setAskMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      setAskMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setAskLoading(false);
      focusInput(askInputRef);
    }
  };

  const patchAskMessage = (index, updater) => {
    setAskMessages((rows) =>
      rows.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const parsed = parseStructuredReply(row.text);
        if (!parsed || typeof parsed !== "object") return row;
        return { ...row, text: JSON.stringify(updater(parsed)), instant: true };
      })
    );
  };

  const saveAskStandards = async (index, data) => {
    if (askLoading) return;
    patchAskMessage(index, (parsed) => ({ ...parsed, saving: true }));
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/save-standards", true, data);
      const { status_code, data: payload, message } = res?.data || {};
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: false,
        saving: false,
        savedStandards:
          payload?.savedStandards || {
            ok: status_code == 1,
            message: message || "Unable to save standards.",
          },
      }));
    } catch {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        saving: false,
        savedStandards: { ok: false, message: "Unable to save standards." },
      }));
    }
  };

  const cancelAskStandards = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingSave: false,
      saving: false,
      discarded: true,
    }));
  };

  const saveAskQuestions = async (index, data) => {
    if (askLoading) return;
    patchAskMessage(index, (parsed) => ({ ...parsed, saving: true }));
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/save-questions", true, data);
      const { status_code, data: payload, message } = res?.data || {};
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: false,
        saving: false,
        savedQuestions:
          payload?.savedQuestions || {
            ok: status_code == 1,
            message: message || "Unable to save questions.",
          },
      }));
    } catch {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        saving: false,
        savedQuestions: { ok: false, message: "Unable to save questions." },
      }));
    }
  };

  const cancelAskQuestions = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingSave: false,
      saving: false,
      discarded: true,
    }));
  };

  const sendAskAi = (e) => {
    e.preventDefault();
    sendAskAiMessage(askInput.trim());
  };

  const openHelp = () => {
    try {
      if (window.$zopim?.livechat?.window?.show) {
        window.$zopim.livechat.window.show();
        return;
      }
    } catch {
      /* ignore */
    }
    try {
      window.zE?.("webWidget", "open");
    } catch {
      /* ignore */
    }
    try {
      window.zE?.("messenger", "open");
    } catch {
      /* ignore */
    }
  };

  const switchAgent = (slug) => {
    setAgentSlug(slug);
    const agent = AGENTS.find((item) => item.slug === slug);
    setAskMessages([
      {
        from: "bot",
        text: `Switched to ${agent?.name}. Ask using this agent's prompt, skills, and knowledge.`,
        instant: true,
      },
    ]);
  };

  const renderPanel = ({
    title,
    onClose,
    childrenHeader,
    listItems,
    listEndRef,
    isLoading,
    formOnSubmit,
    formInputRef,
    formValue,
    formOnChange,
    placeholder,
    onBotChoice,
    onSaveStandards,
    onCancelStandards,
    onSaveQuestions,
    onCancelQuestions,
  }) => (
    <div style={panel}>
      <div style={header}>
        <strong>{title}</strong>
        <button type="button" style={iconBtn} onClick={onClose}>
          ×
        </button>
      </div>
      {childrenHeader}
      <div style={list}>
        {listItems.map((m, i) => (
          <div key={i} style={m.from === "user" ? userBubble : botBubble}>
            {m.from === "bot" ? (
              <WritingReply
                text={m.text}
                instant={Boolean(m.instant) || i !== listItems.length - 1}
                onChoice={onBotChoice}
                onSaveStandards={onSaveStandards ? (payload) => onSaveStandards(i, payload) : undefined}
                onCancelStandards={onCancelStandards ? () => onCancelStandards(i) : undefined}
                onSaveQuestions={onSaveQuestions ? (payload) => onSaveQuestions(i, payload) : undefined}
                onCancelQuestions={onCancelQuestions ? () => onCancelQuestions(i) : undefined}
              />
            ) : (
              m.text
            )}
          </div>
        ))}
        {isLoading ? <GeneratingStatus /> : null}
        <div ref={listEndRef} />
      </div>
      <form onSubmit={formOnSubmit} style={form}>
        <input
          ref={formInputRef}
          style={inputBox}
          value={formValue}
          onChange={formOnChange}
          placeholder={placeholder}
          autoFocus
        />
        <button
          type="submit"
          style={sendBtn}
          disabled={isLoading || !formValue.trim()}
          onMouseDown={(e) => e.preventDefault()}
        >
          Send
        </button>
      </form>
    </div>
  );

  return (
    <>
      {askOpen || open ? (
        <div style={{ ...wrap, bottom: 96 }}>
          {askOpen
            ? renderPanel({
                title: "Ask AI",
                onClose: () => setAskOpen(false),
                childrenHeader: (
                  <div style={agentRow}>
                    {AGENTS.map((agent) => (
                      <button
                        key={agent.slug}
                        type="button"
                        style={agentSlug === agent.slug ? agentChipActive : agentChip}
                        onClick={() => switchAgent(agent.slug)}
                      >
                        {agent.name.replace(" Agent", "")}
                      </button>
                    ))}
                  </div>
                ),
                listItems: askMessages,
                listEndRef: askEndRef,
                isLoading: askLoading,
                formOnSubmit: sendAskAi,
                formInputRef: askInputRef,
                formValue: askInput,
                formOnChange: (e) => setAskInput(e.target.value),
                placeholder: "Ask the selected agent...",
                onBotChoice: sendAskAiMessage,
                onSaveStandards: saveAskStandards,
                onCancelStandards: cancelAskStandards,
                onSaveQuestions: saveAskQuestions,
                onCancelQuestions: cancelAskQuestions,
              })
            : null}

          {open
            ? renderPanel({
                title: "Chat",
                onClose: () => setOpen(false),
                listItems: messages,
                listEndRef: endRef,
                isLoading: loading,
                formOnSubmit: send,
                formInputRef: inputRef,
                formValue: input,
                formOnChange: (e) => setInput(e.target.value),
                placeholder: "Type a message...",
              })
            : null}
        </div>
      ) : null}

      <div style={{ ...wrap, bottom }}>
        <div style={fabStack}>
          {!open && !askOpen && showAskAi ? (
            <button
              type="button"
              style={fab}
              onClick={() => {
                setOpen(false);
                setAskOpen(true);
              }}
            >
              Ask AI
            </button>
          ) : null}
          <button type="button" style={fab} onClick={openHelp}>
            <span style={helpIconWrap} aria-hidden="true">
              ?
            </span>
            Help
          </button>
        </div>
      </div>
    </>
  );
}

const wrap = {
  position: "fixed",
  right: 16,
  bottom: 16,
  zIndex: 1050,
};

const fabStack = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
  gap: 10,
};

const fab = {
  border: 0,
  borderRadius: 999,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  height: 40,
  padding: "0 16px",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  boxShadow: "0 4px 8px rgba(0,0,0,.12), 0 0 2px rgba(0,0,0,.12)",
  cursor: "pointer",
};

const helpIconWrap = {
  width: 16,
  height: 16,
  border: "2px solid #fff",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
};

const panel = {
  width: 420,
  maxWidth: "calc(100vw - 24px)",
  height: 520,
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 8px 28px rgba(0,0,0,.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const header = {
  background: "rgb(142, 68, 173)",
  color: "#fff",
  padding: "10px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const iconBtn = {
  border: 0,
  background: "transparent",
  color: "#fff",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer",
};

const agentRow = {
  display: "flex",
  gap: 6,
  padding: "8px 10px",
  background: "#f8fafc",
  flexWrap: "wrap",
};

const agentChip = {
  border: 0,
  background: "rgba(142, 68, 173, 0.16)",
  color: "#9ca3af",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

const agentChipActive = {
  ...agentChip,
  color: "#000",
};

const list = {
  flex: 1,
  overflowY: "auto",
  padding: 12,
  background: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const botBubble = {
  alignSelf: "flex-start",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "8px 10px",
  maxWidth: "92%",
  fontSize: 14,
  whiteSpace: "normal",
};

const userBubble = {
  ...botBubble,
  alignSelf: "flex-end",
  background: "rgb(142, 68, 173)",
  color: "#fff",
  border: 0,
  whiteSpace: "pre-wrap",
};

const form = {
  display: "flex",
  gap: 8,
  padding: 10,
  borderTop: "1px solid #e2e8f0",
};

const inputBox = {
  flex: 1,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 14,
};

const sendBtn = {
  border: 0,
  borderRadius: 8,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  fontWeight: 600,
  padding: "8px 12px",
  cursor: "pointer",
};
